/**
 * Capacitor 工具函数库
 * 封装平台检测、原生弹窗、安全区域等基础能力。
 * 所有调用前都检查 Capacitor 是否可用（即在原生 WebView 中运行）。
 */
import { Capacitor } from "@capacitor/core";
import { StatusBar, Style as StatusBarStyle } from "@capacitor/status-bar";
import { SplashScreen } from "@capacitor/splash-screen";
import { Camera, CameraResultType, CameraSource } from "@capacitor/camera";
export { CameraSource } from "@capacitor/camera";
import { Preferences } from "@capacitor/preferences";
import { Clipboard } from "@capacitor/clipboard";
import { App as CapApp } from "@capacitor/app";

// ── 平台检测 ──

export const isNativePlatform: boolean =
  typeof Capacitor !== "undefined" && Capacitor.isNativePlatform();

export const platform: string =
  Capacitor.getPlatform(); // "ios" | "android" | "web"

export const isIOS: boolean = platform === "ios";
export const isAndroid: boolean = platform === "android";

// ── 安全区域 ──

export interface SafeAreaInsets {
  top: number;
  bottom: number;
  left: number;
  right: number;
}

/**
 * 获取安全区域（CSS env() 回退）
 */
export function getSafeAreaInsets(): SafeAreaInsets {
  // 尝试从 CSS env() 读取
  const style = getComputedStyle(document.documentElement);
  const parse = (name: string): number => {
    const val = style.getPropertyValue(name).trim();
    if (!val) return 0;
    const num = parseInt(val, 10);
    return isNaN(num) ? 0 : num;
  };

  return {
    top: parse("--sat") || parse("env(safe-area-inset-top)"),
    bottom: parse("--sab") || parse("env(safe-area-inset-bottom)"),
    left: parse("--sal") || parse("env(safe-area-inset-left)"),
    right: parse("--sar") || parse("env(safe-area-inset-right)"),
  };
}

// ── 状态栏 ──

export async function setStatusBarStyle(
  style: "dark" | "light" = "dark",
  overlay: boolean = false
): Promise<void> {
  if (!isNativePlatform) return;
  try {
    await StatusBar.setStyle({
      style: style === "dark" ? StatusBarStyle.Dark : StatusBarStyle.Light,
    });
    if (overlay) {
      await StatusBar.setOverlaysWebView({ overlay: true });
    }
  } catch {
    // 静默忽略
  }
}

export async function showStatusBar(): Promise<void> {
  if (!isNativePlatform) return;
  try {
    await StatusBar.show();
  } catch {
    // 静默忽略
  }
}

export async function hideStatusBar(): Promise<void> {
  if (!isNativePlatform) return;
  try {
    await StatusBar.hide();
  } catch {
    // 静默忽略
  }
}

// ── 启动屏 ──

export async function hideSplash(): Promise<void> {
  if (!isNativePlatform) return;
  try {
    await SplashScreen.hide();
  } catch {
    // 静默忽略
  }
}

// ── 相机 / 相册 ──

export interface CameraResult {
  base64: string;
  dataUrl: string;
  format: string;
}

/**
 * 打开相机或相册选择图片，返回 base64 dataUrl
 */
export async function pickImage(
  source: CameraSource = CameraSource.Photos
): Promise<CameraResult | null> {
  if (!isNativePlatform) {
    // Web fallback：通过 input 选取
    return webPickImage();
  }

  try {
    const image = await Camera.getPhoto({
      quality: 85,
      allowEditing: false,
      resultType: CameraResultType.Base64,
      source,
    });

    if (!image.base64String) return null;

    return {
      base64: image.base64String,
      dataUrl: `data:image/${image.format};base64,${image.base64String}`,
      format: image.format,
    };
  } catch (err: unknown) {
    if (err instanceof Error && err.message?.includes("cancel")) {
      return null; // 用户取消
    }
    throw err;
  }
}

/**
 * Web fallback：通过 input[type="file"] 选图
 */
function webPickImage(): Promise<CameraResult | null> {
  return new Promise((resolve, reject) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";

    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) {
        resolve(null);
        return;
      }

      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        const format = file.type.split("/")[1] || "jpeg";
        const base64 = result.split(",")[1];
        resolve({ base64: base64, dataUrl: result, format });
      };
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    };

    input.oncancel = () => resolve(null);
    input.click();
  });
}

// ── 本地存储（替换 localStorage） ──

export async function storageSet(key: string, value: string): Promise<void> {
  if (isNativePlatform) {
    await Preferences.set({ key, value });
  } else {
    localStorage.setItem(key, value);
  }
}

export async function storageGet(key: string): Promise<string | null> {
  if (isNativePlatform) {
    const { value } = await Preferences.get({ key });
    return value ?? null;
  }
  return localStorage.getItem(key);
}

export async function storageRemove(key: string): Promise<void> {
  if (isNativePlatform) {
    await Preferences.remove({ key });
  } else {
    localStorage.removeItem(key);
  }
}

// ── 剪贴板 ──

export async function copyToClipboard(text: string): Promise<boolean> {
  if (isNativePlatform) {
    try {
      await Clipboard.write({ string: text });
      return true;
    } catch {
      return fallbackCopy(text);
    }
  }

  return fallbackCopy(text);
}

function fallbackCopy(text: string): boolean {
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    document.execCommand("copy");
    document.body.removeChild(ta);
    return true;
  } catch {
    return false;
  }
}

// ── 系统手势返回监听 ──

export function initBackButtonListener(): void {
  if (!isNativePlatform) return;
  
  CapApp.addListener("backButton", () => {
    const pathname = window.location.pathname;
    const isRootPage = pathname === "/" || pathname === "/index.html" || pathname === "" || pathname === "/topics" || pathname === "/treehole" || pathname === "/profile";
    
    if (!isRootPage && window.history.length > 1) {
      window.history.back();
    } else {
      void CapApp.minimizeApp();
    }
  });
}
